import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { FaQuestionCircle } from "react-icons/fa";
import "./TopicsTrendsPage.scss";
import DataWindowSlider from "../components/DataWindowSlider";
import { useDataWindow } from '../hooks/useDataWindow';
import debounce from 'lodash/debounce';
import { getMarkerLabel } from "./markersDictionary";
import ComparisonChart from "../components/ComparisonChart";
import { toJS } from "mobx";
import { fillDataGaps, getActualDataLength } from '../utils/dataUtils';
import { useGlobalFilters } from '../hooks/useGlobalFilters';
import ChartsGroup from "../components/ChartsGroup";

const AGGREGATION_OPTIONS = [
  { value: 1, label: "По дням" },
  { value: 7, label: "Суммировать по неделям" },
  { value: 30, label: "Суммировать по месяцам" },
];

const MARKERS_KEY = 'selectedMarkers_topics';

const CommunityEmotionsCombined = observer(({primaryStore, secondaryStore, themes, groupMode}) => {
  const [selectedMarkers, setSelectedMarkers] = useState(() => {
    try {
      const saved = localStorage.getItem(MARKERS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const globalFilters = useGlobalFilters();

  secondaryStore.setGroupMode(groupMode);
  // Обработка данных с заполнением пропусков
  const processedChartData = useMemo(() => {

    if (!primaryStore.chartsData || primaryStore.chartsData.length === 0) {
      return [];
    }

    // Получаем фильтры из глобальных фильтров или store
    const globalStartDate = globalFilters.startDate;
    const globalEndDate = globalFilters.endDate;
    const storeStartDate = primaryStore.globalFilters?.startDate;
    const storeEndDate = primaryStore.globalFilters?.endDate;
    
    const startDate = globalStartDate || storeStartDate || primaryStore.startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const endDate = globalEndDate || storeEndDate || primaryStore.endDate || new Date().toISOString().split('T')[0];
    
    // Отладочная информация для проверки агрегации
    console.log('CommunityEmotions fillDataGaps params:', {
      globalStartDate,
      globalEndDate,
      storeStartDate,
      storeEndDate,
      finalStartDate: startDate,
      finalEndDate: endDate,
      aggregation: primaryStore.aggregation.toString()
    });

    return fillDataGaps(
      toJS(primaryStore.chartsData), 
      startDate, 
      endDate, 
      primaryStore.aggregation.toString()
    );
  }, [primaryStore.chartsData, primaryStore.startDate, primaryStore.endDate, primaryStore.aggregation]);

  // Проверяем, есть ли обработанные данные для отображения
  const hasProcessedData = useMemo(() => {
    return processedChartData && 
           processedChartData.length > 0 && 
           processedChartData.some(series => series.data && series.data.length > 0);
  }, [processedChartData]);

  const [dataWindow, updateDataWindow] = useDataWindow(100, getActualDataLength(processedChartData));

  const markerColors = useMemo(() => {
    const colors = [
      '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
      '#0288d1', '#c2185b', '#fbc02d', '#455a64', '#0097a7',
    ];
    const colorMap = {};
    selectedMarkers.forEach((marker, index) => {
      colorMap[marker] = colors[index % colors.length];
    });
    return colorMap;
  }, [selectedMarkers]);

  // Дебансированная версия fetchTopicsTrendsData
  const debouncedFetchTopicsTrendsData = useMemo(
    () => debounce((start, end) => primaryStore.fetchTopicsTrendsData(start, end), 300),
    [primaryStore]
  );

  // Очистка дебансированной функции при размонтировании
  useEffect(() => {
    return () => {
      debouncedFetchTopicsTrendsData.cancel();
    };
  }, [debouncedFetchTopicsTrendsData]);

  // Загрузка групп при монтировании
  useEffect(() => {
    primaryStore.fetchGroups();
  }, []);

  // Автоматическая загрузка данных при изменении фильтров
  useEffect(() => {
    debouncedFetchTopicsTrendsData(dataWindow.start, dataWindow.end);
  }, [
    primaryStore.aggregation,
    primaryStore.selectedThemes,
    primaryStore.selectedTheme,
    primaryStore.globalFilters,
    dataWindow,
  ]);

  // Синхронизация глобальных фильтров из localStorage
  useEffect(() => {
    let last = JSON.stringify(primaryStore.globalFilters);
    const interval = setInterval(() => {
      const saved = localStorage.getItem("globalFilters");
      if (saved && saved !== last) {
        last = saved;
        try {
          const parsed = JSON.parse(saved);
          primaryStore.setGlobalFilters({
            selectedGroups: Array.isArray(parsed.selectedGroups) ? parsed.selectedGroups : [],
            startDate: parsed.startDate,
            endDate: parsed.endDate,
          });
        } catch {}
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Обработчики  

  const handleThemeSelect = (key) => {    
      primaryStore.setSelectedTheme(key);
      primaryStore.setThemes([key]);    
  };

  // Проверка на пустые темы
  useEffect(() => {
    if (primaryStore.selectedThemes.length === 0) {
      const available = themes.map(t => t.key);
      const filtered = ["trager_coef", "tgw_positive_emotions"].filter((key) => available.includes(key));
      primaryStore.setThemes(filtered.length === 2 ? filtered : ["trager_coef", "tgw_positive_emotions"]);
    }
  }, [primaryStore.selectedThemes]);

// Обработка данных с заполнением пропусков
const secondaryProcessedChartData = useMemo(() => {
  if (!secondaryStore.chartsData || secondaryStore.chartsData.length === 0) {
    return [];
  }

  // Получаем фильтры из глобальных фильтров или store
  const globalStartDate = globalFilters.startDate;
  const globalEndDate = globalFilters.endDate;
  const storeStartDate = secondaryStore.globalFilters?.startDate;
  const storeEndDate = secondaryStore.globalFilters?.endDate;
  
  const startDate = globalStartDate || storeStartDate || secondaryStore.startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const endDate = globalEndDate || storeEndDate || secondaryStore.endDate || new Date().toISOString().split('T')[0];

  return fillDataGaps(
    toJS(secondaryStore.chartsData), 
    startDate, 
    endDate, 
    secondaryStore.aggregation.toString()
  );
}, [secondaryStore.chartsData, secondaryStore.startDate, secondaryStore.endDate, secondaryStore.aggregation, globalFilters, secondaryStore.globalFilters]);

const secondaryMarkerColors = useMemo(() => {
  const colors = [
    '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
    '#0288d1', '#c2185b', '#fbc02d', '#455a64', '#0097a7',
  ];
  const colorMap = {};
  selectedMarkers.forEach((marker, index) => {
    colorMap[marker] = colors[index % colors.length];
  });
  return colorMap;
}, [selectedMarkers]);

// Дебансированная версия fetchTopicsTrendsData
const debouncedFetchSecondaryTopicsTrendsData = useMemo(
  () => debounce((start, end) => secondaryStore.fetchTopicsTrendsData(start, end), 300),
  [secondaryStore]
);

// Очистка дебансированной функции при размонтировании
useEffect(() => {
  return () => {
    debouncedFetchSecondaryTopicsTrendsData.cancel();
  };
}, [debouncedFetchSecondaryTopicsTrendsData]);

// Загрузка групп при монтировании
useEffect(() => {
  secondaryStore.fetchGroups();
}, []);

// Автоматическая загрузка данных при изменении фильтров
useEffect(() => {
  debouncedFetchSecondaryTopicsTrendsData(dataWindow.start, dataWindow.end);
}, [
  secondaryStore.aggregation,
  secondaryStore.selectedThemes,
  secondaryStore.groupMode,
  secondaryStore.selectedTheme,
  secondaryStore.globalFilters,
  dataWindow,
]);

// Синхронизация глобальных фильтров из localStorage
useEffect(() => {
  let last = JSON.stringify(secondaryStore.globalFilters);
  const interval = setInterval(() => {
    const saved = localStorage.getItem("globalFilters");
    if (saved && saved !== last) {
      last = saved;
      try {
        const parsed = JSON.parse(saved);
        secondaryStore.setGlobalFilters({
          selectedGroups: Array.isArray(parsed.selectedGroups) ? parsed.selectedGroups : [],
          startDate: parsed.startDate,
          endDate: parsed.endDate,
        });
      } catch {}
    }
  }, 500);
  return () => clearInterval(interval);
}, []);

const handleSecondaryThemeSelect = (key) => {    
  if (secondaryStore.groupMode === "separate") {
    secondaryStore.setSelectedTheme(key);
    secondaryStore.setThemes([key]);
  }

};

// Обработчики
const handleSecondaryThemeToggle = (key) => {
  if (secondaryStore.groupMode === "sum") {
    secondaryStore.setThemes(
      secondaryStore.selectedThemes.includes(key)
        ? secondaryStore.selectedThemes.filter((k) => k !== key)
        : [...secondaryStore.selectedThemes, key]
    );
  }
};

// Проверка на пустые темы
useEffect(() => {
  if (secondaryStore.selectedThemes.length === 0) {
    const available = themes.map(t => t.key);
    const filtered = ["trager_coef", "tgw_positive_emotions"].filter((key) => available.includes(key));
    secondaryStore.setThemes(filtered.length === 2 ? filtered : ["trager_coef", "tgw_positive_emotions"]);
  }
}, [secondaryStore.selectedThemes]);

  return (
    <div className="topics-trends-container">
      <div
        className="filters-panel"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 32,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            gap: 12,
            minWidth: 220,
            width: 340,
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", gap: 16, flex: 1 }}>
            <div className="filter-group" style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "center" }}>
              <label className="filter-label">Агрегация:</label>
              <select
                value={primaryStore.aggregation}
                onChange={(e) => {
                  primaryStore.setAggregation(Number(e.target.value))
                  secondaryStore.setAggregation(Number(e.target.value))
                } }
                className="select select--wide"
                style={{ height: 36, minWidth: 180 }}
              >
                {AGGREGATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>            
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flex: 1,
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div className="themes-container" style={{ display: "flex", flexDirection: "row", gap: 16, flex: 1 }}>
             
              <div className="filter-group" style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "center"  }}>
                <label className="filter-label">Тема:</label>
                <select
                  value={primaryStore.selectedTheme}
                  onChange={(e) => handleThemeSelect(e.target.value)}
                  className="select"
                >
                  {themes.map((theme) => (
                    <option key={theme.key} value={theme.key}>
                      {theme.label}
                    </option>
                  ))}
                </select>
                <div className="help-icon" title={themes.find(t => t.key === primaryStore.selectedTheme)?.description || ""}>
                  <FaQuestionCircle />
                </div>
              </div>
            
          </div>
        </div>
      </div>
      
      <div className="chart-container">        
        { primaryStore.globalFilters.selectedGroups.length === 0? (
          <p className="no-data">Выберите сообщества в верхней панели для отображения графика</p>
        ) : primaryStore.error ? (
          <div className="error-message">{primaryStore.error}</div>
        ) : !hasProcessedData ? (
          primaryStore.loading ? (
              <div className="no-data-message">Загрузка</div>
            ) : (
            <div className="no-data-message">Нет данных для отображения</div>
          )          
        ) : primaryStore.loading ? (
          <div className="no-data-message">Загрузка данных для выбранного диапазона</div>
        ) : (
          <>             
            <DataWindowSlider
              aggregation={primaryStore.aggregation}
              actualDataLength={getActualDataLength(processedChartData, primaryStore.aggregation.toString())}
            />         
            <div style={{ position: 'relative', zIndex: 1 }}>
              <ComparisonChart
                loading = {primaryStore.loading}
                chartData={processedChartData}
                dataWindow={dataWindow}                
                getMarkerLabelFunction={getMarkerLabel}
                getTableDataByDate={(date) => primaryStore.fetchTableDataByDate(date)}
              />
            </div>
            <div className="themes-container" style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "center"  }}>
              {secondaryStore.groupMode === "sum" ? (
                themes.map((theme) => (
                  <div key={theme.key} className="theme-item">
                    <label className="theme-checkbox">
                      <input
                        type="checkbox"
                        checked={secondaryStore.selectedThemes.includes(theme.key)}
                        onChange={() => handleSecondaryThemeToggle(theme.key)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-label">{theme.label}</span>
                    </label>
                    <div className="help-icon" title={theme.description}>
                      <FaQuestionCircle />
                    </div>
                  </div>
                ))
              ) : (
                <div className="filter-group" style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "center"  }}>
                  <label className="filter-label">Тема:</label>
                  <select
                    value={secondaryStore.selectedTheme}
                    onChange={(e) => handleSecondaryThemeSelect(e.target.value)}
                    className="select"
                  >
                    {themes.map((theme) => (
                      <option key={theme.key} value={theme.key}>
                        {theme.label}
                      </option>
                    ))}
                  </select>
                  <div className="help-icon" title={themes.find(t => t.key === secondaryStore.selectedTheme)?.description || ""}>
                    <FaQuestionCircle />
                  </div>
                </div>
              )}
            </div>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <ChartsGroup
                loading = {secondaryStore.loading}
                chartData={secondaryProcessedChartData}
                dataWindow={dataWindow}
                markerColors={secondaryMarkerColors}
                getMarkerLabelFunction={getMarkerLabel}
                getTableDataByDate={(date) => secondaryStore.getTableDataByDate(date)}
                hideDateTitle={true}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default CommunityEmotionsCombined;